import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Storage } from '@ionic/storage-angular';
import { firstValueFrom } from 'rxjs';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { environment } from '../../environments/environment';

export function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

export function generateRandomString(length: number): string {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

export async function generateCodeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}

export function parseCallbackUrl(url: string): { code?: string; state?: string; error?: string } {
  const queryStart = url.indexOf('?');
  if (queryStart === -1) {
    return {};
  }
  const params = new URLSearchParams(url.substring(queryStart + 1));
  return {
    code: params.get('code') ?? undefined,
    state: params.get('state') ?? undefined,
    error: params.get('error') ?? undefined
  };
}

export const NATIVE_AUTH_REDIRECT_URI = 'com.vivent.vita1://callback';

const ACCESS_TOKEN_KEY = 'vivent_native_access_token';
const REFRESH_TOKEN_KEY = 'vivent_native_refresh_token';
const ID_TOKEN_KEY = 'vivent_native_id_token';
const TOKEN_EXPIRY_KEY = 'vivent_native_token_expiry';
const PKCE_VERIFIER_KEY = 'vivent_native_pkce_verifier';
const PKCE_STATE_KEY = 'vivent_native_pkce_state';

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  id_token: string;
  expires_in: number;
}

@Injectable({ providedIn: 'root' })
export class NativeAuthService {
  private readonly tokenEndpoint =
    `${environment.keycloak.url}/realms/${environment.keycloak.realm}/protocol/openid-connect/token`;
  private readonly authEndpoint =
    `${environment.keycloak.url}/realms/${environment.keycloak.realm}/protocol/openid-connect/auth`;
  private readonly logoutEndpoint =
    `${environment.keycloak.url}/realms/${environment.keycloak.realm}/protocol/openid-connect/logout`;

  private pendingLogin?: { resolve: () => void; reject: (err: Error) => void };
  private listenerRegistered = false;

  constructor(
    private readonly http: HttpClient,
    private readonly storage: Storage
  ) {}

  initialize(): void {
    if (this.listenerRegistered) {
      return;
    }
    this.listenerRegistered = true;
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => this.handleAppUrlOpen(event.url));
  }

  async login(): Promise<void> {
    const verifier = generateRandomString(64);
    const state = generateRandomString(16);
    const challenge = await generateCodeChallenge(verifier);

    await this.storage.set(PKCE_VERIFIER_KEY, verifier);
    await this.storage.set(PKCE_STATE_KEY, state);

    const authorizeUrl = `${this.authEndpoint}?` + new URLSearchParams({
      response_type: 'code',
      client_id: environment.keycloak.clientId,
      redirect_uri: NATIVE_AUTH_REDIRECT_URI,
      scope: 'openid',
      code_challenge: challenge,
      code_challenge_method: 'S256',
      state
    }).toString();

    return new Promise<void>((resolve, reject) => {
      this.pendingLogin = { resolve, reject };
      Browser.open({ url: authorizeUrl });
    });
  }

  async logout(): Promise<void> {
    const idToken = await this.storage.get(ID_TOKEN_KEY);
    await this.clearTokens();

    const params: Record<string, string> = {
      client_id: environment.keycloak.clientId,
      post_logout_redirect_uri: NATIVE_AUTH_REDIRECT_URI
    };
    if (idToken) {
      params['id_token_hint'] = idToken;
    }

    await Browser.open({ url: `${this.logoutEndpoint}?${new URLSearchParams(params).toString()}` });
  }

  async ensureValidToken(): Promise<boolean> {
    const accessToken = await this.storage.get(ACCESS_TOKEN_KEY);
    const expiry = Number(await this.storage.get(TOKEN_EXPIRY_KEY));

    if (accessToken && expiry && Date.now() < expiry - 10000) {
      return true;
    }

    const refreshToken = await this.storage.get(REFRESH_TOKEN_KEY);
    if (!refreshToken) {
      return false;
    }

    try {
      await this.refreshAccessToken(refreshToken);
      return true;
    } catch {
      await this.clearTokens();
      return false;
    }
  }

  async getAccessToken(): Promise<string | undefined> {
    return (await this.storage.get(ACCESS_TOKEN_KEY)) ?? undefined;
  }

  private async handleAppUrlOpen(url: string): Promise<void> {
    if (!url.startsWith(NATIVE_AUTH_REDIRECT_URI)) {
      return;
    }

    await Browser.close();

    const { code, state, error } = parseCallbackUrl(url);
    if (!code) {
      return;
    }

    const pending = this.pendingLogin;
    this.pendingLogin = undefined;

    try {
      const expectedState = await this.storage.get(PKCE_STATE_KEY);
      if (error || state !== expectedState) {
        throw new Error(error || 'Login callback state mismatch');
      }

      const verifier = await this.storage.get(PKCE_VERIFIER_KEY);
      await this.exchangeCodeForTokens(code, verifier);
      pending?.resolve();
    } catch (err) {
      pending?.reject(err instanceof Error ? err : new Error('Login failed'));
    }
  }

  private async exchangeCodeForTokens(code: string, verifier: string): Promise<void> {
    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      code,
      redirect_uri: NATIVE_AUTH_REDIRECT_URI,
      client_id: environment.keycloak.clientId,
      code_verifier: verifier
    });

    const response = await firstValueFrom(
      this.http.post<TokenResponse>(this.tokenEndpoint, body.toString(), {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      })
    );

    await this.storeTokens(response);
  }

  private async refreshAccessToken(refreshToken: string): Promise<void> {
    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
      client_id: environment.keycloak.clientId
    });

    const response = await firstValueFrom(
      this.http.post<TokenResponse>(this.tokenEndpoint, body.toString(), {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      })
    );

    await this.storeTokens(response);
  }

  private async storeTokens(response: TokenResponse): Promise<void> {
    const expiry = Date.now() + response.expires_in * 1000;
    await this.storage.set(ACCESS_TOKEN_KEY, response.access_token);
    await this.storage.set(REFRESH_TOKEN_KEY, response.refresh_token);
    await this.storage.set(ID_TOKEN_KEY, response.id_token);
    await this.storage.set(TOKEN_EXPIRY_KEY, expiry);
  }

  private async clearTokens(): Promise<void> {
    await this.storage.remove(ACCESS_TOKEN_KEY);
    await this.storage.remove(REFRESH_TOKEN_KEY);
    await this.storage.remove(ID_TOKEN_KEY);
    await this.storage.remove(TOKEN_EXPIRY_KEY);
  }
}
